import { Compare } from '@flowus/common';
import { cx } from '@flowus/common/cx';
import { useStableCallback } from '@flowus/common/hooks/react-utils';
import { formula } from '@flowus/formula';
import type { TextMarker } from 'codemirror';
import dayjs from 'dayjs';
import isHotkey from 'is-hotkey';
import { css } from 'otion';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useBitable } from 'src/bitable/context';
import { Button } from 'src/common/components/button';
import { DATE_TIME_FORMAT } from 'src/common/const';
import { KeyboardShortcut } from 'src/components/keyboard-shortcut';
import { useBlockLocked } from 'src/hooks/block/use-block-locked';
import type { FormulaError, FormulaPreview } from 'src/hooks/block/use-formula-tool';
import { getFormulaTool } from 'src/hooks/block/use-formula-tool';
import { useCollectionId } from 'src/hooks/collection-view/use-collection-id';
import { useViewParentId } from 'src/hooks/collection-view/use-collection-view';
import { useReadonly } from 'src/hooks/page';
import { useTransaction } from 'src/hooks/use-transaction';
import { updateSchema } from 'src/redux/managers/block/update';
import { getState } from 'src/redux/store';
import { useObservableBlock } from 'src/services/rxjs-redux/hook';
import { useFuSelector } from 'src/services/store-context/use-fine-context-selector';
import { CheckboxValue } from '../checkbox';
import { NumValue } from '../num';
import { TextValue, TextValueView } from '../text';
import type { CellEditorProps, CellViewProps } from '../types';
import type { FormulaCodeEditorHandle } from './formula-code-editor';
import { FormulaCodeEditor, getSearchWord } from './formula-code-editor';
import { FormulaManual } from './formula-manual';

const DateValue: FC<CellViewProps> = ({ className, propertyId, recordId }) => {
  const { collectionId } = useBitable();
  const value = useFuSelector(
    (state) => {
      const formulaTool = getFormulaTool(collectionId, state);

      return formulaTool.getValue(recordId, propertyId) as Date | null;
    },
    {
      equalityFn: Compare.deriveEqualFunc(Compare.nullsFirst(Compare.by((it) => it.getTime()))),
    }
  );
  if (value == null) return null;
  return (
    <TextValueView className={cx('p-2 leading-5', className)}>
      {dayjs(value).format(DATE_TIME_FORMAT)}
    </TextValueView>
  );
};

export const FormulaValue: FC<CellViewProps> = (props) => {
  const { collectionId } = useBitable();
  const { propertyId } = props;
  const type = useFuSelector((state) => {
    const formulaTool = getFormulaTool(collectionId, state);

    return formulaTool.getType(propertyId);
  });
  if (type === formula.ValueTool.numberType) {
    return <NumValue {...props} />;
  } else if (type === formula.ValueTool.stringType) {
    return <TextValue {...props} />;
  } else if (type === formula.ValueTool.booleanType) {
    return <CheckboxValue {...props} />;
  } else if (type === formula.ValueTool.dateType) {
    return <DateValue {...props} />;
  }
  return null;
};

export const FormulaEditor: FC<CellEditorProps> = ({ propertyId, recordId, onClose, viewId }) => {
  const collectionId = useCollectionId(recordId) ?? '';
  const formulaTool = getFormulaTool(collectionId);
  const collectionReadonly = useReadonly(collectionId);
  const viewParentId = useViewParentId(viewId) ?? collectionId;
  const isLocked = useBlockLocked(viewParentId);
  const readonly = collectionReadonly || isLocked;
  const initialValue = useObservableBlock(collectionId, (block) => {
    const formula = block?.data.schema?.[propertyId]?.formula;
    return formula == null ? '' : formulaTool.fromServer(formula as any);
  });
  const editorRef = useRef<FormulaCodeEditorHandle>();
  const [search, setSearch] = useState<{ cursor: number; word: string } | null>(null);

  const markerRef = useRef<TextMarker | undefined>();
  const [result, setResult] = useState<FormulaPreview | FormulaError | null>(null);

  const transaction = useTransaction();
  const onSave = () => {
    if (editorRef.current == null) return;
    if (result?.type !== 'preview') return;

    const value = editorRef.current.cm().getValue();
    transaction(() => {
      const { blocks } = getState();
      const collection = blocks[collectionId];
      if (!collection) return;

      const schema = collection.data.schema ?? {};
      const propertySchema = schema[propertyId];
      if (!propertySchema) return;

      updateSchema(collection.uuid, {
        [propertyId]: {
          ...propertySchema,
          formula: formulaTool.toServer(value) as any,
        },
      });
    });
    onClose();
  };

  const handleChange = useStableCallback(() => {
    if (editorRef.current == null) return;
    const editor = editorRef.current.cm();

    const search = getSearchWord(editor);
    setSearch(search);

    const setResult2 = (result: FormulaPreview | FormulaError | null) => {
      setResult(result);
      if (markerRef.current != null) {
        markerRef.current.clear();
        markerRef.current = undefined;
      }
      if (result == null) return;
      if (result.type === 'error') {
        const doc = editor.getDoc();
        markerRef.current = editor.markText(
          doc.posFromIndex(result.pos.pmin),
          doc.posFromIndex(result.pos.pmax),
          { className: 'cm-mark-error' }
        );
      }
    };

    const value = editor.getValue();
    if (value === '') {
      setResult2(null);
      return;
    }

    const result = formulaTool.validate(value, recordId, propertyId);
    setResult2(result);
  });

  useEffect(() => {
    handleChange();
  }, [handleChange]);

  return (
    <div
      className={cx(
        'bg-white1 flex flex-col',
        css({
          minWidth: '180px',
          width: '580px',
          height: readonly ? '48px' : '400px',
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: '70vh',
        })
      )}
    >
      <div
        className="flex items-start max-h-[180px] formula-editor"
        onKeyDown={(event) => {
          event.stopPropagation();
          if (isHotkey('mod+enter')(event) && result?.type !== 'error') {
            onSave();
          }
        }}
      >
        <FormulaCodeEditor
          ref={editorRef}
          className="flex-1 min-w-0 mx-2.5 my-2 text-t2 border border-grey6 bg-grey9 focus-within-border rounded-sm box-border"
          autoFocus
          placeholder="输入公式"
          initialValue={initialValue}
          maxHeight="162px"
          readOnly={readonly}
          onChange={handleChange}
        />
        {!readonly && (
          <Button
            colorType="active"
            className="my-2 mr-2.5"
            onClick={onSave}
            disable={result?.type === 'error'}
          >
            提交
          </Button>
        )}
      </div>
      {!readonly && (
        <>
          <FormulaManual
            className="border-t border-grey6 flex-1"
            search={search?.word}
            recordId={recordId}
            propId={propertyId}
            onComplete={(kind, name) => {
              const cm = editorRef.current?.cm();
              if (cm == null) return;
              if (kind === 'function') {
                name += '(';
              } else if (kind === 'property') {
                name = `prop(${JSON.stringify(name)})`;
              }
              const doc = cm.getDoc();
              const from = search?.cursor ?? doc.indexFromPos(doc.getCursor());
              const to = search == null ? from : from + search.word.length;
              cm.replaceRange(name, doc.posFromIndex(from), doc.posFromIndex(to), '+input');
            }}
          />
          <div className="h-8 text-t2 flex items-center border-t border-grey6">
            <span className="flex-1 mx-3 text-ellipsis">
              {result?.type === 'preview' && <span>预览结果：{result.preview}</span>}
              {result?.type === 'error' && (
                <span className="text-red flex-1">{result.message}</span>
              )}
            </span>
            <KeyboardShortcut className="mr-3" shortcut="Enter可快捷提交" />
          </div>
        </>
      )}
    </div>
  );
};
